import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Shield, Eye, EyeOff, CheckCircle2, XCircle, AlertCircle, Key, Plus, Edit, Trash2, Loader2, Video, BookOpen } from 'lucide-react';
import ZoomSetupGuideModal from './ZoomSetupGuideModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = {
  label: '',
  zoom_account_id: '',
  s2s_client_id: '',
  s2s_client_secret: '',
  sdk_key: '',
  sdk_secret: '',
  default_zoom_user_id: '',
};

const ZoomAccountManagement = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(null); // account id currently being tested
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showSecrets, setShowSecrets] = useState({ s2s: false, sdk: false });

  // Password verification dialog
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);

  // Setup guide modal
  const [showSetupGuide, setShowSetupGuide] = useState(false);

  useEffect(() => { fetchAccounts(); }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const resp = await axios.get(`${API}/admin/zoom-accounts`);
      setAccounts(resp.data || []);
    } catch (err) {
      console.error('Failed to load Zoom accounts', err);
      toast.error('Failed to load Zoom accounts');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowSecrets({ s2s: false, sdk: false });
    setShowDialog(true);
  };

  const openEdit = (account) => {
    setEditingId(account.id);
    // Do NOT pre-populate secrets — user must re-enter to change.
    setForm({
      label: account.label,
      zoom_account_id: '',
      s2s_client_id: '',
      s2s_client_secret: '',
      sdk_key: '',
      sdk_secret: '',
      default_zoom_user_id: account.default_zoom_user_id || '',
    });
    setShowSecrets({ s2s: false, sdk: false });
    setShowDialog(true);
  };

  const validateForm = () => {
    if (!form.label.trim()) return 'Label is required';
    if (!editingId) {
      // Full validation only for create
      if (!form.zoom_account_id.trim()) return 'Zoom Account ID is required';
      if (!form.s2s_client_id.trim()) return 'S2S Client ID is required';
      if (!form.s2s_client_secret.trim()) return 'S2S Client Secret is required';
      if (!form.sdk_key.trim()) return 'Meeting SDK Key is required';
      if (!form.sdk_secret.trim()) return 'Meeting SDK Secret is required';
      if (!form.default_zoom_user_id.trim()) return 'Default Zoom User (email or userId) is required';
    }
    return null;
  };

  const handleSaveClick = () => {
    const err = validateForm();
    if (err) {
      toast.error(err);
      return;
    }
    setShowPasswordDialog(true);
  };

  const handleSaveWithPassword = async () => {
    if (!adminPassword) {
      toast.error('Please enter your password');
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        // Build partial update — only include changed non-empty fields
        const payload = { admin_password: adminPassword };
        for (const k of ['label', 'zoom_account_id', 's2s_client_id', 's2s_client_secret',
                          'sdk_key', 'sdk_secret', 'default_zoom_user_id']) {
          if (form[k] && form[k].trim()) payload[k] = form[k].trim();
        }
        await axios.put(`${API}/admin/zoom-accounts/${editingId}`, payload);
        toast.success('Zoom account updated');
      } else {
        await axios.post(`${API}/admin/zoom-accounts`, { ...form, admin_password: adminPassword });
        toast.success('Zoom account added');
      }
      setShowPasswordDialog(false);
      setShowDialog(false);
      setAdminPassword('');
      setForm(EMPTY_FORM);
      setEditingId(null);
      await fetchAccounts();
    } catch (err) {
      if (err.response?.status === 403) toast.error('Invalid password');
      else toast.error(err.response?.data?.detail || 'Failed to save Zoom account');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (id) => {
    try {
      setTesting(id);
      await axios.post(`${API}/admin/zoom-accounts/${id}/test`);
      toast.success('Zoom credentials are valid');
      await fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test failed');
      await fetchAccounts();
    } finally {
      setTesting(null);
    }
  };

  const handleDeactivate = async (id, label) => {
    if (!window.confirm(`Deactivate Zoom license "${label}"? Active classes bound to it stay scheduled, but new classes can't use it.`)) return;
    try {
      await axios.delete(`${API}/admin/zoom-accounts/${id}`);
      toast.success('Zoom account deactivated');
      await fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to deactivate');
    }
  };

  return (
    <>
      <Card data-testid="zoom-accounts-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Video className="w-5 h-5" />
                Zoom Licenses
              </CardTitle>
              <CardDescription className="mt-2">
                Manage Server-to-Server OAuth + Meeting SDK credentials. When scheduling a class, the system can auto-pick an active license or you can choose one manually.
              </CardDescription>
            </div>
            <Button onClick={openCreate} data-testid="zoom-account-add-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Zoom License
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {/* Setup guide CTA */}
          <div className="p-4 mb-4 bg-gradient-to-r from-[#0A7EA4]/5 to-[#FF6B6B]/5 border border-[#0A7EA4]/20 rounded-lg flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-2 flex-1 min-w-[240px]">
              <BookOpen className="w-5 h-5 text-[#0A7EA4] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-gray-900">First time setting up Zoom?</p>
                <p className="text-xs text-gray-600 mt-0.5">Step-by-step guide covering both Marketplace apps, exact scopes, domain allowlisting, and publish-vs-development guidance.</p>
              </div>
            </div>
            <Button onClick={() => setShowSetupGuide(true)} variant="outline"
              className="border-[#0A7EA4] text-[#0A7EA4] hover:bg-[#0A7EA4]/5"
              data-testid="zoom-setup-guide-btn">
              <BookOpen className="w-4 h-4 mr-2" />
              Open Setup Guide
            </Button>
          </div>

          {/* Security notice */}
          <div className="p-4 mb-4 bg-yellow-50 border border-yellow-200 rounded-lg flex gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <p className="font-semibold mb-1">Where to obtain these credentials</p>
              <ul className="list-disc list-inside space-y-1">
                <li><strong>Server-to-Server OAuth</strong>: Zoom Marketplace &rarr; Develop &rarr; Build Server-to-Server App &rarr; App Credentials (Account ID, Client ID, Client Secret)</li>
                <li><strong>Meeting SDK</strong>: Zoom Marketplace &rarr; Develop &rarr; Build App &rarr; enable Meeting SDK under Embed &rarr; App Credentials (Client ID = SDK Key, Client Secret = SDK Secret)</li>
                <li><strong>Default Zoom User</strong>: the Zoom email (or userId) under which meetings will be scheduled &mdash; typically the account owner</li>
              </ul>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : accounts.length === 0 ? (
            <div className="text-center py-12 text-gray-500" data-testid="zoom-accounts-empty">
              <Video className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No Zoom licenses configured yet.</p>
              <p className="text-sm mt-1">Add one to start scheduling live classes.</p>
            </div>
          ) : (
            <Table data-testid="zoom-accounts-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>S2S Account</TableHead>
                  <TableHead>SDK Key</TableHead>
                  <TableHead>Default User</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Test</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map(a => (
                  <TableRow key={a.id} data-testid={`zoom-account-row-${a.id}`}>
                    <TableCell className="font-medium">{a.label}</TableCell>
                    <TableCell className="font-mono text-xs">{a.zoom_account_id_masked}</TableCell>
                    <TableCell className="font-mono text-xs">{a.sdk_key_masked}</TableCell>
                    <TableCell className="text-sm">{a.default_zoom_user_id}</TableCell>
                    <TableCell>
                      {a.is_active ? (
                        <Badge className="bg-green-600 text-white"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge>
                      ) : (
                        <Badge variant="secondary"><XCircle className="w-3 h-3 mr-1" />Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {a.last_test_status === 'success' && (
                        <span className="text-green-600">✓ Valid</span>
                      )}
                      {a.last_test_status === 'failed' && (
                        <span className="text-red-600" title={a.last_test_error}>✗ Failed</span>
                      )}
                      {!a.last_test_status && <span className="text-gray-400">Never</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => handleTest(a.id)}
                          disabled={testing === a.id}
                          data-testid={`zoom-account-test-${a.id}`}
                        >
                          {testing === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Test'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(a)} data-testid={`zoom-account-edit-${a.id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        {a.is_active && (
                          <Button size="sm" variant="ghost" onClick={() => handleDeactivate(a.id, a.label)} data-testid={`zoom-account-delete-${a.id}`}>
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5" />
              {editingId ? 'Edit Zoom License' : 'Add Zoom License'}
            </DialogTitle>
            <DialogDescription>
              {editingId
                ? 'Leave secret fields blank to keep existing values.'
                : 'Enter your Zoom app credentials. Secrets are encrypted before storage.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label>License Label *</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. HelmTorq Primary License"
                data-testid="zoom-form-label"
              />
            </div>

            <div className="pt-2 border-t">
              <h4 className="font-semibold text-sm text-gray-700 mb-3">Server-to-Server OAuth (for scheduling meetings)</h4>
              <div className="space-y-3">
                <div>
                  <Label>Zoom Account ID {!editingId && '*'}</Label>
                  <Input
                    value={form.zoom_account_id}
                    onChange={(e) => setForm({ ...form, zoom_account_id: e.target.value })}
                    placeholder="e.g. abc123XYZ"
                    className="font-mono"
                    data-testid="zoom-form-account-id"
                  />
                </div>
                <div>
                  <Label>S2S Client ID {!editingId && '*'}</Label>
                  <Input
                    value={form.s2s_client_id}
                    onChange={(e) => setForm({ ...form, s2s_client_id: e.target.value })}
                    className="font-mono"
                    data-testid="zoom-form-s2s-client-id"
                  />
                </div>
                <div>
                  <Label>S2S Client Secret {!editingId && '*'}</Label>
                  <div className="relative">
                    <Input
                      type={showSecrets.s2s ? 'text' : 'password'}
                      value={form.s2s_client_secret}
                      onChange={(e) => setForm({ ...form, s2s_client_secret: e.target.value })}
                      placeholder={editingId ? 'Leave blank to keep existing' : 'Enter secret'}
                      className="font-mono pr-10"
                      data-testid="zoom-form-s2s-secret"
                    />
                    <button type="button" onClick={() => setShowSecrets({ ...showSecrets, s2s: !showSecrets.s2s })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showSecrets.s2s ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t">
              <h4 className="font-semibold text-sm text-gray-700 mb-3">Meeting SDK (for embedded join)</h4>
              <div className="space-y-3">
                <div>
                  <Label>Meeting SDK Key (= app Client ID) {!editingId && '*'}</Label>
                  <Input
                    value={form.sdk_key}
                    onChange={(e) => setForm({ ...form, sdk_key: e.target.value })}
                    className="font-mono"
                    data-testid="zoom-form-sdk-key"
                  />
                </div>
                <div>
                  <Label>Meeting SDK Secret (= app Client Secret) {!editingId && '*'}</Label>
                  <div className="relative">
                    <Input
                      type={showSecrets.sdk ? 'text' : 'password'}
                      value={form.sdk_secret}
                      onChange={(e) => setForm({ ...form, sdk_secret: e.target.value })}
                      placeholder={editingId ? 'Leave blank to keep existing' : 'Enter secret'}
                      className="font-mono pr-10"
                      data-testid="zoom-form-sdk-secret"
                    />
                    <button type="button" onClick={() => setShowSecrets({ ...showSecrets, sdk: !showSecrets.sdk })}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showSecrets.sdk ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t">
              <Label>Default Zoom User (email or userId) {!editingId && '*'}</Label>
              <Input
                value={form.default_zoom_user_id}
                onChange={(e) => setForm({ ...form, default_zoom_user_id: e.target.value })}
                placeholder="e.g. host@yourorg.com"
                data-testid="zoom-form-default-user"
              />
              <p className="text-xs text-gray-500 mt-1">Meetings will be scheduled under this Zoom user&apos;s account.</p>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">Cancel</Button>
            <Button onClick={handleSaveClick} className="flex-1" data-testid="zoom-form-save-btn">
              <Shield className="w-4 h-4 mr-2" />
              {editingId ? 'Update' : 'Add'} (Requires Password)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password verify dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Verify Your Password
            </DialogTitle>
            <DialogDescription>
              Confirm your admin password to {editingId ? 'update' : 'save'} this Zoom license.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Admin Password *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="pr-10"
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveWithPassword()}
                  data-testid="zoom-password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => { setShowPasswordDialog(false); setAdminPassword(''); }} className="flex-1">Cancel</Button>
            <Button onClick={handleSaveWithPassword} disabled={saving || !adminPassword} className="flex-1" data-testid="zoom-password-confirm-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : 'Confirm & Save'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Setup guide modal */}
      <ZoomSetupGuideModal open={showSetupGuide} onOpenChange={setShowSetupGuide} />
    </>
  );
};

export default ZoomAccountManagement;
